/**
 * NewAvenantModal — 1-click amendment for the « Ouverture et
 * animation de zone » avenant (iter110n). Text is 100% verbatim from
 * the CEO reference doc; only zone + dates are editable, with sane
 * defaults pulled from the base contract.
 */
import { useState } from "react";
import { FileSignature, Loader2, X } from "lucide-react";
import { toast } from "sonner";

import { api } from "../../lib/api";


function todayIso() {
    return new Date().toISOString().slice(0, 10);
}


export default function NewAvenantModal({ pid, contract, onClose, onDone }) {
    const [form, setForm] = useState({
        zone: "Grand Est",
        effective_date: todayIso(),
        end_date: contract?.end_date || "",
    });
    const [saving, setSaving] = useState(false);

    const valid = form.zone.trim() && form.effective_date && form.end_date;

    async function submit() {
        if (!valid) {
            toast.error("Zone et dates obligatoires");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post(`/permanents/${pid}/avenants`, {
                contract_id: contract.id,
                zone: form.zone.trim(),
                effective_date: form.effective_date,
                end_date: form.end_date,
            });
            toast.success(`Avenant N°${data.number} généré · PDF prêt`);
            onDone?.(data);
        } catch (e) {
            toast.error("Création impossible : " + (e?.response?.data?.detail || e.message));
        } finally {
            setSaving(false);
        }
    }

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
            onClick={onClose}
            data-testid="new-avenant-modal"
        >
            <div
                className="w-full max-w-lg bg-white rounded-3xl shadow-2xl overflow-hidden"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex items-start justify-between gap-4 px-6 py-5 border-b border-slate-100">
                    <div className="flex items-start gap-3">
                        <div className="h-11 w-11 rounded-2xl bg-shariti-orange-soft text-shariti-orange flex items-center justify-center shrink-0">
                            <FileSignature className="h-5 w-5" />
                        </div>
                        <div>
                            <h2 className="text-lg font-black text-shariti-black">
                                Nouvel avenant — Ouverture de zone
                            </h2>
                            <p className="text-[12px] text-slate-500 mt-0.5 leading-snug">
                                Texte 100 % verbatim de l'avenant SHARITI (12 articles, forfait 1&nbsp;150 €/mois,
                                commission 10 €/bulletin coord, retenue 20 %). Seuls la zone et les dates se règlent
                                ici.
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        data-testid="new-avenant-close"
                        className="h-8 w-8 rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100 inline-flex items-center justify-center transition"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                <div className="px-6 py-5 space-y-3">
                    <Field label="Zone d'affectation (Article 3)">
                        <input
                            value={form.zone}
                            onChange={(e) => setForm({ ...form, zone: e.target.value })}
                            data-testid="new-avenant-zone"
                            placeholder="Grand Est"
                            className="input-shariti"
                        />
                    </Field>
                    <div className="grid grid-cols-2 gap-3">
                        <Field label="Prise d'effet *">
                            <input
                                type="date"
                                value={form.effective_date}
                                onChange={(e) => setForm({ ...form, effective_date: e.target.value })}
                                data-testid="new-avenant-effective-date"
                                className="input-shariti"
                            />
                        </Field>
                        <Field label="Terme (aligné sur le contrat) *">
                            <input
                                type="date"
                                value={form.end_date}
                                onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                                data-testid="new-avenant-end-date"
                                className="input-shariti"
                            />
                        </Field>
                    </div>
                    <div className="rounded-2xl bg-amber-50 border border-amber-200 p-3 text-[11px] text-amber-900 leading-snug">
                        <b>Rappel</b> — Le forfait 1&nbsp;150&nbsp;€/mois, la grille bulletins nets
                        (35 € / 50 € / 65 €), la commission de coordination 10&nbsp;€, la retenue de garantie
                        de 20&nbsp;% et l'avance de 575&nbsp;€ sont figés dans le modèle CEO. Après signature
                        électronique, le PDF est archivé automatiquement dans le dossier du prestataire.
                    </div>
                </div>

                <div className="border-t border-slate-100 px-6 py-4 flex items-center justify-end gap-2">
                    <button
                        onClick={onClose}
                        className="rounded-full border border-slate-200 px-4 py-2 text-[12px] font-bold text-slate-600 hover:bg-slate-50"
                    >
                        Annuler
                    </button>
                    <button
                        onClick={submit}
                        disabled={!valid || saving}
                        data-testid="new-avenant-submit-btn"
                        className="rounded-full bg-shariti-orange text-white px-5 py-2 text-[12px] font-black uppercase tracking-widest hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed inline-flex items-center gap-2"
                    >
                        {saving && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                        Générer l'avenant
                    </button>
                </div>
            </div>
        </div>
    );
}


function Field({ label, children, full = false }) {
    return (
        <div className={full ? "col-span-2" : ""}>
            <label className="block text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1">
                {label}
            </label>
            {children}
        </div>
    );
}
